import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import round from 'lodash/round'

import { DropdownMenu } from '@opentrons/components'
import {
  POSITION_REFERENCE_BOTTOM,
  POSITION_REFERENCE_CENTER,
  POSITION_REFERENCE_TOP,
} from '@opentrons/shared-data'

import type { Dispatch, SetStateAction } from 'react'
import type { DropdownOption } from '@opentrons/components'
import type { PositionReference } from '@opentrons/shared-data'

interface UsePositionReferenceResult {
  positionReferenceDropdown: JSX.Element
  reference: PositionReference
  setReference: Dispatch<SetStateAction<PositionReference>>
}

export function usePositionReference(args: {
  zValue: number
  updateZValue: Dispatch<SetStateAction<string>>
  wellDepth: number
  initialReference?: unknown
}): UsePositionReferenceResult {
  const { initialReference, zValue, updateZValue, wellDepth } = args
  const { t } = useTranslation('modal')
  const [reference, setReference] = useState<PositionReference>(
    initialReference != null
      ? (initialReference as PositionReference)
      : POSITION_REFERENCE_BOTTOM
  )

  const handleUpdateReference = (
    oldReference: PositionReference,
    newReference: PositionReference,
    zValue: number
  ): void => {
    let newZValue = zValue
    if (oldReference === POSITION_REFERENCE_BOTTOM) {
      switch (newReference) {
        case POSITION_REFERENCE_CENTER:
          newZValue = zValue - wellDepth / 2
          break
        case POSITION_REFERENCE_TOP:
          newZValue = zValue - wellDepth
          break
        default:
          break
      }
    } else if (oldReference === POSITION_REFERENCE_CENTER) {
      switch (newReference) {
        case POSITION_REFERENCE_BOTTOM:
          newZValue = zValue + wellDepth / 2
          break
        case POSITION_REFERENCE_TOP:
          newZValue = zValue - wellDepth / 2
          break
        default:
          break
      }
    } else if (oldReference === POSITION_REFERENCE_TOP) {
      switch (newReference) {
        case POSITION_REFERENCE_BOTTOM:
          newZValue = zValue + wellDepth
          break
        case POSITION_REFERENCE_CENTER:
          newZValue = zValue + wellDepth / 2
          break
        default:
          break
      }
    }
    updateZValue(String(round(newZValue, 1)))
  }

  const referenceOptions: DropdownOption[] = [
    {
      name: t(`tip_position.position_references.${POSITION_REFERENCE_TOP}`),
      value: POSITION_REFERENCE_TOP,
    },
    {
      name: t(`tip_position.position_references.${POSITION_REFERENCE_CENTER}`),
      value: POSITION_REFERENCE_CENTER,
    },
    {
      name: t(`tip_position.position_references.${POSITION_REFERENCE_BOTTOM}`),
      value: POSITION_REFERENCE_BOTTOM,
    },
  ]
  return {
    positionReferenceDropdown: (
      <DropdownMenu
        title={t('tip_position.field_titles.reference_position')}
        dropdownType="neutral"
        width="100%"
        currentOption={
          referenceOptions.find(({ value }) => value === reference) ??
          referenceOptions[0]
        }
        filterOptions={referenceOptions}
        onClick={e => {
          const newReference = e as PositionReference
          handleUpdateReference(reference, newReference, zValue)
          setReference(e as PositionReference)
        }}
      />
    ),
    reference,
    setReference,
  }
}
